import {
  EmployeeCardEmployeeWithGaps,
  getActualEmployeePositions,
  getEmployeePositionSlotByNumber,
  isFiredEmployeePosition,
} from "~/entity/Employee/lib/restGaps";

import {
  TEmployeePosition,
  TEmployeeProfileUser,
  TEmployeeView,
} from "./types";

const mapUser = (
  user?: {
    id?: string | number | null;
    login?: string | null;
    email?: string | null;
    phone?: string | null;
    fullname?: string | null;
    role?: string | null;
    roleRu?: string | null;
    Employee?: { id?: string | number | null } | null;
  } | null,
): TEmployeeProfileUser | null => {
  if (!user) {
    return null;
  }

  return {
    id: user.id,
    login: user.login,
    email: user.email,
    phone: user.phone,
    fullname: user.fullname,
    role: user.role,
    roleRu: user.roleRu || user.role,
    Employee: user.Employee,
  };
};

const mapDealer = (
  row: EmployeeCardEmployeeWithGaps["positions"][number]["activePositions"][number],
) => ({
  id: String(row.id),
  InstanceId: row.graphqlInstanceId,
  DealerId: String(row.dealerId),
  Dealer: {
    DealerName:
      row.bindingType === "PARTNER"
        ? row.organization?.name
        : row.dealer?.dealerName,
  },
  bindingType: row.bindingType === "PARTNER" ? "PARTNER" : "DEALER",
  email: row.email,
  DataConsent: row.dataConsent,
  status: row.status,
  lastStatusUpdateByUser: mapUser(row.lastStatusUpdateByUser),
  lastStatusUpdateAt: row.lastStatusUpdateAt,
  hireByUser: mapUser(row.hireByUser),
  fireByUser: mapUser(row.fireByUser),
  positionOrder: row.positionOrder,
  startDate: row.startDate,
  startOrderNumber: row.startOrderNumber,
  endDate: row.endDate,
  endOrderNumber: row.endOrderNumber,
  jobDescription: row.jobDescription,
});

export const mapEmployeeCardToView = (
  employee: EmployeeCardEmployeeWithGaps,
): TEmployeeView => ({
  id: employee.id,
  status: employee.status,
  surname: employee.surname,
  name: employee.name,
  middleName: employee.middleName,
  gender: employee.gender === false ? "female" : "male",
  birthDay: employee.birthDay,
  workPhone: employee.workPhone,
  Photo: employee.photo,
  Consents: [],
  ResourcesControlUserId: employee.resourcesControlUserId,
  ladaAcademiaId: employee.ladaAcademiaId,
  createdAt: employee.createdAt,
  lastEditDate: employee.lastEditDate,
  HiredDate: employee.hiredDate,
  CreatedByUser: mapUser(employee.createdByUser),
  LastEditByUser: mapUser(employee.lastEditByUser),
});

export const mapEmployeeCardToViewPositions = (
  employee: EmployeeCardEmployeeWithGaps,
): TEmployeePosition[] =>
  (employee.positions || [])
    .map((group) => {
      const latest = getActualEmployeePositions(group.activePositions);
      const actual = latest.filter((row) => !isFiredEmployeePosition(row));
      const number =
        getEmployeePositionSlotByNumber(
          (actual[0] || latest[0])?.number || 1,
        ) || "one";
      const inactive = [
        ...latest.filter(isFiredEmployeePosition),
        ...(group.inactivePositions || []),
      ];

      return {
        number,
        ActivePosition: actual.length
          ? {
              PositionsId: String(group.positionId),
              Position: {
                id: String(group.position?.id ?? group.positionId),
                code: group.position?.code,
                name: group.position?.name,
                DirectionId: group.directionId,
                Direction: {
                  role: group.directionRole || [],
                },
              },
              Education: {
                training: group.educationTraining,
                certificate: group.educationCertificate,
              },
              Positions: actual.map(mapDealer),
            }
          : null,
        InactivePositions: inactive.length
          ? [
              {
                PositionsId: String(group.positionId),
                Position: {
                  id: String(group.position?.id ?? group.positionId),
                  code: group.position?.code,
                  name: group.position?.name,
                },
                Education: {
                  training: group.educationTraining,
                  certificate: group.educationCertificate,
                },
                Positions: inactive.map(mapDealer),
              },
            ]
          : [],
      };
    })
    .filter(
      (position) =>
        position.ActivePosition || position.InactivePositions?.length,
    );
